import React, { useState, useRef, useEffect } from 'react'
import { extractDominantColor, generateIcons } from '../../utils/functions'
import MobileIconGenerator from './MobileIconGenerator'
import DesktopIconGenerator from './DesktopIconGenerator'

const IconGeneratorWrapper = () => {
  // Shared state
  const [uploadedImage, setUploadedImage] = useState(null)
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [iconName, setIconName] = useState('ic_launcher')
  const [padding, setPadding] = useState(0)
  const [platforms, setPlatforms] = useState(['ios', 'android'])
  const [outputFormat, setOutputFormat] = useState('png')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [showBadge, setShowBadge] = useState(false)
  const [badgeText, setBadgeText] = useState('')
  const [badgeColor, setBadgeColor] = useState('#333333')
  const [monochromeImage, setMonochromeImage] = useState(null)
  const [showMonochrome, setShowMonochrome] = useState(false)
  const colorInputRef = useRef(null)

  // Auto-scroll to top and prevent unwanted scroll behavior on page load
  useEffect(() => {
    // Force scroll to top immediately
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    
    // Prevent scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
    
    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto'
      }
    }
  }, [])

  // Auto-focus the color input and extract color when image is uploaded
  useEffect(() => {
    if (uploadedImage && colorInputRef.current) {
      // Extract dominant color from image
      extractDominantColor(uploadedImage).then((dominantColor) => {
        setBackgroundColor(dominantColor)
      })
      
      // Only auto-focus/click if this is not the initial page load and on desktop
      // This prevents unwanted scroll behavior on page reload and disables for mobile
      const isInitialLoad = !document.hasFocus()
      const isMobile = window.innerWidth < 1024 // lg breakpoint
      
      if (!isInitialLoad && !isMobile) {
        // Delay to allow animation to complete
        const timer = setTimeout(() => {
          if (colorInputRef.current && document.hasFocus()) {
            colorInputRef.current.focus()
            colorInputRef.current.click() // This opens the color picker on most browsers
          }
        }, 400) // Slightly longer than animation duration
        
        return () => clearTimeout(timer)
      }
    }
  }, [uploadedImage])

  const handleImageUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      setUploadedImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  const handleMonochromeImageUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      setMonochromeImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  const handleGenerate = async (format = outputFormat) => {
    setIsGenerating(true)
    
    try {
      const monochromeToUse = showMonochrome ? monochromeImage : null
      await generateIcons(uploadedImage, backgroundColor, iconName, padding, showBadge, badgeText, badgeColor, format, monochromeToUse)
    } catch (error) {
      console.error('Error generating icons:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const sharedProps = {
    uploadedImage,
    backgroundColor,
    setBackgroundColor,
    iconName,
    setIconName,
    padding,
    setPadding,
    platforms,
    setPlatforms,
    outputFormat,
    setOutputFormat,
    onImageUpload: handleImageUpload,
    handleGenerate,
    isGenerating,
    showToast,
    setShowToast,
    colorInputRef,
    showBadge,
    setShowBadge,
    badgeText,
    setBadgeText,
    badgeColor,
    setBadgeColor,
    monochromeImage,
    setMonochromeImage,
    onMonochromeImageUpload: handleMonochromeImageUpload,
    showMonochrome,
    setShowMonochrome
  }

  return (
    <section id="editor">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0 select-none">
        {/* Mobile Version - Shown on mobile/tablet */}
        <div className="block lg:hidden">
          <MobileIconGenerator {...sharedProps} />
        </div>

        {/* Desktop Version - Shown on desktop */}
        <div className="hidden lg:block">
          <DesktopIconGenerator {...sharedProps} />
        </div>
      </div>
    </section>
  )
}

export default IconGeneratorWrapper 